import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import type { Dimension, DimensionKey, LoadResult, Totals } from './types';

const useStyles = makeStyles(theme => ({
  card: { height: '100%' },
  headline: {
    fontSize: '2rem',
    fontWeight: 700,
    margin: 0,
  },
  metric: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  baseline: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  note: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
}));

interface MetricSpec {
  label: string;
  /** Totals key to read. */
  key: string;
  unit?: '%';
}

interface CardSpec {
  dimension: DimensionKey;
  title: string;
  /** The first metric is the headline number; the rest are detail rows. */
  metrics: MetricSpec[];
  /** Totals key holding a free-text note shown under the numbers. */
  noteKey?: string;
}

export const CARD_SPECS: CardSpec[] = [
  {
    dimension: 'techdocs',
    title: 'TechDocs coverage',
    metrics: [
      { label: 'Covered (of known)', key: 'pct_of_known', unit: '%' },
      { label: 'Repos covered', key: 'covered' },
      { label: 'Repos uncovered', key: 'uncovered' },
      // RUB-51304: sites the TechDocs fallback built from READMEs. Not in
      // `covered`, so they do not raise pct_of_known.
      { label: 'Repos on default site', key: 'fallback' },
      { label: 'Repos unknown', key: 'unknown' },
      { label: 'Docs older than 30 days', key: 'stale_gt_30d' },
    ],
  },
  {
    dimension: 'api_spec',
    title: 'API spec coverage',
    metrics: [
      { label: 'Covered (of known)', key: 'pct_of_known', unit: '%' },
      { label: 'Repos with a spec gap', key: 'gap_repos' },
      { label: 'Apps without a spec', key: 'gap_apps_exact' },
      { label: 'Repos unknown', key: 'unknown' },
    ],
  },
  {
    dimension: 'dependencies',
    title: 'Dependencies declared vs proposed',
    metrics: [
      { label: 'Declared edges', key: 'declared_edges' },
      { label: 'Proposed candidates', key: 'proposed_candidates' },
      {
        label: 'Components with declared edges',
        key: 'components_with_declared_edges',
      },
    ],
  },
  {
    dimension: 'mcp_usage',
    title: 'MCP usage',
    metrics: [
      { label: 'MCP Lambda invocations (7d)', key: 'lambda_invocations_7d' },
      { label: 'Tool invocations (7d)', key: 'tool_invocations_7d' },
      { label: 'Tool errors (7d)', key: 'tool_errors_7d' },
    ],
    noteKey: 'note',
  },
];

function numberAt(totals: Totals | null | undefined, key: string) {
  const value = totals?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * RUB-50964: below this many known repos (in_scope - unknown) a
 * `pct_of_known` is a percentage over a handful of samples, so the card
 * withholds it instead of headlining e.g. "0.0%" over one measured repo.
 */
export const MIN_KNOWN_FOR_PCT = 10;

/** The totals key whose value is covered / (in_scope - unknown) × 100. */
const PCT_OF_KNOWN_KEY = 'pct_of_known';

export interface PctOfKnown {
  /** The percentage to render; null when missing or withheld. */
  value: number | null;
  /** True when fewer than MIN_KNOWN_FOR_PCT repos are known. */
  withheld: boolean;
  /** Repos with a known status; null when the totals lack the counts. */
  known: number | null;
  inScope: number | null;
  unknown: number | null;
  covered: number | null;
}

/**
 * Reads `pct_of_known` together with the counts it is based on. A snapshot
 * without in_scope/unknown keeps the old behaviour (percentage shown, no
 * measured count) rather than hiding a value it cannot judge.
 */
export function pctOfKnown(totals: Totals | null | undefined): PctOfKnown {
  const pct = numberAt(totals, PCT_OF_KNOWN_KEY);
  const inScope = numberAt(totals, 'in_scope');
  const rawUnknown = numberAt(totals, 'unknown');
  const covered = numberAt(totals, 'covered');
  // Clamp bad data (unknown > in_scope, or negative counts) so the caption
  // never reads "250 of 241 repos unknown" and known is never negative.
  const unknown =
    inScope !== null && rawUnknown !== null
      ? Math.min(Math.max(rawUnknown, 0), Math.max(inScope, 0))
      : rawUnknown;
  const known =
    inScope !== null && unknown !== null
      ? Math.max(inScope - unknown, 0)
      : null;
  const withheld = known !== null && known < MIN_KNOWN_FOR_PCT;
  return {
    value: withheld ? null : pct,
    withheld,
    known,
    inScope,
    unknown,
    covered,
  };
}

/** "8 of 107 measured": covered repos out of the known ones. */
export function measuredText(basis: PctOfKnown): string | null {
  if (basis.withheld || basis.known === null) return null;
  const known = basis.known.toLocaleString('en');
  return basis.covered === null
    ? `${known} measured`
    : `${basis.covered.toLocaleString('en')} of ${known} measured`;
}

/** "Not yet measured fleet-wide: 240 of 241 repos unknown". */
export function withheldText(basis: PctOfKnown): string | null {
  if (!basis.withheld || basis.inScope === null || basis.unknown === null) {
    return null;
  }
  if (basis.inScope <= 0) return null;
  return `Not yet measured fleet-wide: ${basis.unknown.toLocaleString(
    'en',
  )} of ${basis.inScope.toLocaleString('en')} repos unknown`;
}

/** A metric's value as the card shows it (pct_of_known may be withheld). */
function metricValue(
  totals: Totals | null | undefined,
  spec: MetricSpec,
): number | null {
  return spec.key === PCT_OF_KNOWN_KEY
    ? pctOfKnown(totals).value
    : numberAt(totals, spec.key);
}

export function formatValue(value: number | null, unit?: '%'): string {
  if (value === null) return 'n/a';
  return unit === '%' ? `${value.toFixed(1)}%` : value.toLocaleString('en');
}

/** Signed difference; percentages are percentage points. */
export function formatDelta(
  latest: number | null,
  baseline: number | null,
  unit?: '%',
): string | null {
  if (latest === null || baseline === null) return null;
  const delta = Math.round((latest - baseline) * 10) / 10;
  const sign = delta > 0 ? '+' : '';
  const body = unit === '%' ? `${delta.toFixed(1)} pp` : `${delta}`;
  return delta === 0 ? `±0${unit === '%' ? ' pp' : ''}` : `${sign}${body}`;
}

function baselineText(
  spec: MetricSpec,
  latest: number | null,
  baselineDim: Dimension | undefined,
  baseline: LoadResult,
): string {
  if (baseline.kind === 'not-found') return 'No baseline recorded yet';
  if (baseline.kind !== 'ok') return 'Baseline unavailable';
  if (!baselineDim || baselineDim.status !== 'ok') {
    return 'Baseline value unavailable for this dimension';
  }
  // Both sides go through the same withholding rule, so a withheld latest
  // or baseline value never produces a delta.
  const value = metricValue(baselineDim.totals, spec);
  const delta = formatDelta(latest, value, spec.unit);
  return `Baseline ${formatValue(value, spec.unit)}${
    delta ? ` (${delta})` : ''
  }`;
}

function SummaryCard({
  spec,
  latest,
  baselineDim,
  baseline,
}: {
  spec: CardSpec;
  latest: Dimension | undefined;
  baselineDim: Dimension | undefined;
  baseline: LoadResult;
}) {
  const classes = useStyles();
  const [headline, ...details] = spec.metrics;

  let body: JSX.Element;
  if (!latest) {
    body = (
      <Alert severity="warning">
        This dimension is missing from the snapshot.
      </Alert>
    );
  } else if (latest.status !== 'ok') {
    body = (
      <Alert severity="warning">
        <strong>{latest.status}</strong>
        {latest.error ? `: ${latest.error}` : ''}
      </Alert>
    );
  } else {
    const headlineValue = headline
      ? metricValue(latest.totals, headline)
      : null;
    const basis =
      headline?.key === PCT_OF_KNOWN_KEY ? pctOfKnown(latest.totals) : null;
    const measured = basis ? measuredText(basis) : null;
    const withheldNote = basis ? withheldText(basis) : null;
    const note = spec.noteKey ? latest.totals?.[spec.noteKey] : undefined;
    body = (
      <>
        {headline && (
          <>
            <Typography variant="body2" color="textSecondary">
              {headline.label}
            </Typography>
            <Typography component="p" className={classes.headline}>
              {formatValue(headlineValue, headline.unit)}
            </Typography>
            {measured && (
              <Typography variant="body2" color="textSecondary">
                {measured}
              </Typography>
            )}
            {withheldNote && (
              <Typography
                variant="caption"
                component="p"
                className={classes.note}
              >
                {withheldNote}
              </Typography>
            )}
            <Typography variant="body2" className={classes.baseline}>
              {baselineText(headline, headlineValue, baselineDim, baseline)}
            </Typography>
          </>
        )}
        {details.map(metric => {
          const value = metricValue(latest.totals, metric);
          const baselineValue =
            baseline.kind === 'ok' && baselineDim?.status === 'ok'
              ? metricValue(baselineDim.totals, metric)
              : null;
          const delta = formatDelta(value, baselineValue, metric.unit);
          return (
            <Typography
              key={metric.key}
              variant="body2"
              className={classes.metric}
              component="div"
            >
              <span>{metric.label}</span>
              <span>
                {formatValue(value, metric.unit)}
                {delta ? ` (${delta})` : ''}
              </span>
            </Typography>
          );
        })}
        {typeof note === 'string' && note && (
          <Typography variant="caption" component="p" className={classes.note}>
            {note}
          </Typography>
        )}
      </>
    );
  }

  return (
    <Card className={classes.card} data-testid={`card-${spec.dimension}`}>
      {/* h3: the cards sit under the page's "Fleet summary" h2. */}
      <CardHeader
        title={spec.title}
        titleTypographyProps={{ variant: 'h6', component: 'h3' }}
      />
      <CardContent>{body}</CardContent>
    </Card>
  );
}

export function SummaryCards({
  latest,
  baseline,
}: {
  latest: Partial<Record<DimensionKey, Dimension>>;
  baseline: LoadResult;
}) {
  const baselineDims =
    baseline.kind === 'ok' ? baseline.data.snapshot.dimensions : {};
  return (
    <Grid container spacing={3}>
      {CARD_SPECS.map(spec => (
        <Grid item xs={12} sm={6} lg={3} key={spec.dimension}>
          <SummaryCard
            spec={spec}
            latest={latest[spec.dimension]}
            baselineDim={baselineDims?.[spec.dimension]}
            baseline={baseline}
          />
        </Grid>
      ))}
    </Grid>
  );
}
